import { useState, useEffect } from "react";
import { auth, applyEmailVerificationCode, verifyPasswordResetOobCode, confirmNewPasswordWithCode, resendVerificationEmail } from "./firebase";
import { useTheme } from "./theme";
import { useT } from "./i18n";
import { GLOBAL_CSS } from "./styles";
import { isolateInterpolation } from "./utils";

function isInvalidOrExpired(code) {
  return code === "auth/invalid-action-code" || code === "auth/expired-action-code";
}

function goHome() {
  window.location.href = "/";
}

// ─── Shared card chrome — same Babi-branded card as VerifyEmailScreen ─────────
function ActionCard({ dk, isRTL, children }) {
  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <div dir={isRTL ? "rtl" : "ltr"} style={{
        minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center",
        background: dk ? "#12080A" : "#FDF6EE", fontFamily:"'Nunito',sans-serif", padding:"40px 20px",
      }}>
        <div style={{
          width:"100%", maxWidth:420,
          background: dk ? "#1F1212" : "#fff",
          border:`1px solid ${dk?"#3A2020":"#F0DFC0"}`,
          borderRadius:22, padding:"44px 40px", textAlign:"center",
          boxShadow:"0 8px 40px rgba(123,30,30,0.10)",
        }}>
          <div style={{width:56,height:56,borderRadius:"50%",overflow:"hidden",margin:"0 auto 20px",boxShadow:"0 4px 24px rgba(123,30,30,0.18)"}}>
            <img src="/babi%20fantasy2.jpg?v=2" alt="Babi, your AI tutor" style={{width:"100%",height:"100%",objectFit:"cover"}} />
          </div>
          {children}
        </div>
      </div>
    </>
  );
}

// ─── Brand-red card — used for the post-verification success state ───────────
function BrandCard({ dk, isRTL, children }) {
  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <div dir={isRTL ? "rtl" : "ltr"} style={{
        minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center",
        background: dk ? "#12080A" : "#FDF6EE", fontFamily:"'Nunito',sans-serif", padding:"40px 20px",
      }}>
        <div style={{
          width:"100%", maxWidth:420,
          background:"linear-gradient(150deg,#6B1515 0%,#8B2020 55%,#7B1E1E 100%)",
          borderRadius:22, padding:"44px 40px", textAlign:"center",
          boxShadow:"0 8px 40px rgba(123,30,30,0.28)",
        }}>
          <div style={{
            display:"inline-flex", alignItems:"center", justifyContent:"center",
            background:"#fff", borderRadius:14, padding:"10px 22px", marginBottom:24,
            boxShadow:"0 4px 20px rgba(0,0,0,0.15)",
          }}>
            <img src="/speafy-logo-rectangular-bg-removed-200x50.png" alt="Speafy" style={{height:30,display:"block"}} />
          </div>
          {children}
        </div>
      </div>
    </>
  );
}

function BrandTitle({ children }) {
  return (
    <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:"1.625rem",fontWeight:700,color:"#fff",marginBottom:12}}>
      {children}
    </h2>
  );
}

function BrandBody({ children }) {
  return (
    <p style={{fontSize:"0.9375rem",color:"rgba(255,255,255,0.85)",lineHeight:1.7,marginBottom:28}}>
      {children}
    </p>
  );
}

function BrandButton({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        width:"100%", padding:"13px 20px", marginBottom:12,
        background:"#fff", color:"#7B1E1E",
        border:"none", borderRadius:12, cursor:"pointer",
        fontFamily:"'Nunito',sans-serif", fontSize:"0.9375rem", fontWeight:700,
      }}>
      {children}
    </button>
  );
}

function CardTitle({ dk, children }) {
  return (
    <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:"1.625rem",fontWeight:700,color:dk?"#E07070":"#6B1515",marginBottom:12}}>
      {children}
    </h2>
  );
}

function CardBody({ dk, children }) {
  return (
    <p style={{fontSize:"0.9375rem",color:dk?"#9A7060":"#5C3D2E",lineHeight:1.7,marginBottom:28}}>
      {children}
    </p>
  );
}

function PrimaryButton({ onClick, disabled, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width:"100%", padding:"13px 20px", marginBottom:12,
        background:"linear-gradient(135deg,#7B1E1E,#A33030)", color:"#fff",
        border:"none", borderRadius:12, cursor: disabled ? "not-allowed" : "pointer",
        fontFamily:"'Nunito',sans-serif", fontSize:"0.9375rem", fontWeight:700,
        opacity: disabled ? 0.6 : 1,
      }}>
      {children}
    </button>
  );
}

function LinkButton({ dk, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{background:"none",border:"none",cursor:"pointer",fontFamily:"'Nunito',sans-serif",fontSize:"0.8125rem",fontWeight:700,color:dk?"#E07070":"#7B1E1E",textDecoration:"underline",padding:0}}>
      {children}
    </button>
  );
}

function ErrorText({ children }) {
  return <p style={{color:"#c0392b",fontSize:"0.8125rem",marginTop:0,marginBottom:12,textAlign:"center"}}>{children}</p>;
}

// ─── mode=verifyEmail ──────────────────────────────────────────────────────────
function VerifyEmailAction({ oobCode, dk, isRTL, t }) {
  const [status, setStatus] = useState("working"); // working | success | error
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState("");

  useEffect(() => {
    if (!oobCode) { setStatus("error"); return; }
    let cancelled = false;
    applyEmailVerificationCode(oobCode)
      .then(async () => {
        // Refresh the local user object so a same-session return to the app
        // (via goHome below) sees emailVerified: true immediately, instead of
        // relying on a stale cached token.
        if (auth?.currentUser) {
          try { await auth.currentUser.reload(); } catch { /* non-fatal */ }
        }
        if (!cancelled) setStatus("success");
      })
      .catch(async (e) => {
        console.error("Email verification error:", e.code, e.message);
        // TEMPORARY, until the project's stuck Action URL config is fixed on
        // Google's side (support ticket pending — see project memory):
        // Firebase's own default action-handler page still does the actual
        // verification and consumes the oobCode itself, then its "Continue"
        // button sends the user here with that same, now-stale code. Under
        // that setup there is currently no way to land on this route in
        // verifyEmail mode EXCEPT via that already-succeeded redirect, so an
        // invalid/expired code here means "already verified", not "failed" —
        // regardless of whether this browser has a live session to confirm
        // against (it may not, e.g. incognito or a different browser/device
        // than the one that signed up). Once the Action URL fix lands, this
        // route will receive fresh, real codes again and this branch should
        // be revisited/removed.
        if (isInvalidOrExpired(e.code)) {
          if (auth?.currentUser) {
            try { await auth.currentUser.reload(); } catch { /* non-fatal */ }
          }
          if (!cancelled) setStatus("success");
          return;
        }
        if (!cancelled) setStatus("error");
      });
    return () => { cancelled = true; };
  }, [oobCode]);

  const handleResend = async () => {
    setResendError(""); setResent(false);
    try {
      await resendVerificationEmail();
      setResent(true);
    } catch {
      setResendError(t("authAction.resendError"));
    }
  };

  if (status === "working") {
    return (
      <ActionCard dk={dk} isRTL={isRTL}>
        <CardTitle dk={dk}>{t("authAction.verifyingTitle")}</CardTitle>
      </ActionCard>
    );
  }

  if (status === "success") {
    return (
      <BrandCard dk={dk} isRTL={isRTL}>
        <div style={{fontSize:"2.5rem",marginBottom:16}}>✅</div>
        <BrandTitle>{t("authAction.verifiedTitle")}</BrandTitle>
        <BrandBody>{t("authAction.verifiedBody")}</BrandBody>
        <BrandButton onClick={goHome}>{t("authAction.continueBtn")}</BrandButton>
      </BrandCard>
    );
  }

  // status === "error"
  return (
    <ActionCard dk={dk} isRTL={isRTL}>
      <div style={{fontSize:"2.5rem",marginBottom:16}}>⚠️</div>
      <CardTitle dk={dk}>{t("authAction.verifyErrorTitle")}</CardTitle>
      <CardBody dk={dk}>{t("authAction.verifyErrorBody")}</CardBody>
      {resendError && <ErrorText>{resendError}</ErrorText>}
      {auth?.currentUser ? (
        <PrimaryButton onClick={handleResend}>
          {resent ? t("authAction.resendSent") : t("authAction.resendBtn")}
        </PrimaryButton>
      ) : (
        <PrimaryButton onClick={goHome}>{t("authAction.signInBtn")}</PrimaryButton>
      )}
    </ActionCard>
  );
}

// ─── mode=resetPassword ────────────────────────────────────────────────────────
function ResetPasswordAction({ oobCode, dk, t }) {
  const [status, setStatus] = useState("verifying"); // verifying | form | submitting | success | error
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!oobCode) { setStatus("error"); return; }
    let cancelled = false;
    verifyPasswordResetOobCode(oobCode)
      .then((resolvedEmail) => {
        if (cancelled) return;
        setEmail(resolvedEmail || "");
        setStatus("form");
      })
      .catch((e) => {
        console.error("Password reset code verification error:", e.code, e.message);
        if (!cancelled) setStatus("error");
      });
    return () => { cancelled = true; };
  }, [oobCode]);

  const handleSubmit = async () => {
    if (password.length < 6) { setFormError(t("authAction.passwordTooShort")); return; }
    if (password !== confirmPw) { setFormError(t("authAction.passwordMismatch")); return; }
    setFormError(""); setStatus("submitting");
    try {
      await confirmNewPasswordWithCode(oobCode, password);
      setStatus("success");
    } catch (e) {
      console.error("Confirm password reset error:", e.code, e.message);
      if (isInvalidOrExpired(e.code)) {
        setStatus("error");
      } else {
        setFormError(t("authAction.genericError"));
        setStatus("form");
      }
    }
  };

  const inputStyle = {
    width:"100%", padding:"12px 14px", boxSizing:"border-box", marginBottom:10,
    background:dk?"#1C1010":"#fff", border:`1.5px solid ${dk?"#4A2828":"#E8D5B7"}`, borderRadius:10,
    fontFamily:"'Nunito',sans-serif", fontSize:"1rem", color:dk?"#F0E0D0":"#2C1810",
    outline:"none",
  };

  if (status === "verifying") {
    return <CardTitle dk={dk}>{t("authAction.verifyingReset")}</CardTitle>;
  }

  if (status === "form" || status === "submitting") {
    return (
      <>
        <CardTitle dk={dk}>{t("authAction.resetTitle")}</CardTitle>
        <CardBody dk={dk}>{isolateInterpolation(t("authAction.resetBodyFor"), "email", email)}</CardBody>
        <input
          type="password"
          placeholder={t("authAction.newPasswordPh")}
          value={password}
          onChange={e => { setPassword(e.target.value); setFormError(""); }}
          disabled={status === "submitting"}
          style={inputStyle}
          autoComplete="new-password"
        />
        <input
          type="password"
          placeholder={t("authAction.confirmPasswordPh")}
          value={confirmPw}
          onChange={e => { setConfirmPw(e.target.value); setFormError(""); }}
          onKeyDown={e => e.key === "Enter" && handleSubmit()}
          disabled={status === "submitting"}
          style={inputStyle}
          autoComplete="new-password"
        />
        {formError && <ErrorText>{formError}</ErrorText>}
        <PrimaryButton onClick={handleSubmit} disabled={status === "submitting"}>
          {status === "submitting" ? t("authAction.resettingBtn") : t("authAction.resetSubmitBtn")}
        </PrimaryButton>
      </>
    );
  }

  if (status === "success") {
    return (
      <>
        <div style={{fontSize:"2.5rem",marginBottom:16}}>✅</div>
        <CardTitle dk={dk}>{t("authAction.resetSuccessTitle")}</CardTitle>
        <CardBody dk={dk}>{t("authAction.resetSuccessBody")}</CardBody>
        <PrimaryButton onClick={goHome}>{t("authAction.signInBtn")}</PrimaryButton>
      </>
    );
  }

  // status === "error"
  return (
    <>
      <div style={{fontSize:"2.5rem",marginBottom:16}}>⚠️</div>
      <CardTitle dk={dk}>{t("authAction.resetErrorTitle")}</CardTitle>
      <CardBody dk={dk}>{t("authAction.resetErrorBody")}</CardBody>
      <PrimaryButton onClick={goHome}>{t("authAction.backToSignIn")}</PrimaryButton>
    </>
  );
}

// ─── Genuinely unrecognized mode ────────────────────────────────────────────────
function InvalidModeAction({ dk, t }) {
  return (
    <>
      <div style={{fontSize:"2.5rem",marginBottom:16}}>⚠️</div>
      <CardTitle dk={dk}>{t("authAction.invalidTitle")}</CardTitle>
      <CardBody dk={dk}>{t("authAction.invalidBody")}</CardBody>
      <LinkButton dk={dk} onClick={goHome}>{t("authAction.backToSignIn")}</LinkButton>
    </>
  );
}

// ─── mode entirely missing (no query string) ────────────────────────────────────
// TEMPORARY, until the project's stuck Action URL config is fixed on Google's
// side (support ticket pending — see project memory): Firebase's own default
// action-handler page does the actual verify/reset itself, then its "Continue"
// button sends the user to our bare continueUrl with NO query string at all —
// not even mode/oobCode. So landing here with no mode currently means "Firebase
// already finished successfully", not "invalid link". Once the Action URL fix
// lands, this route will start receiving real mode=.../oobCode=... links again
// and this branch should be revisited/removed.
function NoModeAction({ dk, isRTL, t }) {
  return (
    <BrandCard dk={dk} isRTL={isRTL}>
      <div style={{fontSize:"2.5rem",marginBottom:16}}>✅</div>
      <BrandTitle>{t("authAction.verifiedTitle")}</BrandTitle>
      <BrandBody>{t("authAction.verifiedBody")}</BrandBody>
      <BrandButton onClick={goHome}>{t("authAction.continueBtn")}</BrandButton>
    </BrandCard>
  );
}

// ─── Entry point ────────────────────────────────────────────────────────────────
// Handles Firebase Auth email action links (verifyEmail / resetPassword) landed
// on the custom domain at /auth/action?mode=...&oobCode=... — see App.js for the
// early route check that renders this ahead of the normal screen state machine.
export function AuthActionScreen() {
  const { t, isRTL } = useT();
  const { darkMode } = useTheme();
  const [params] = useState(() => new URLSearchParams(window.location.search));
  const mode = params.get("mode");
  const oobCode = params.get("oobCode");

  if (mode === "verifyEmail") {
    return <VerifyEmailAction oobCode={oobCode} dk={darkMode} isRTL={isRTL} t={t} />;
  }

  if (!mode) {
    return <NoModeAction dk={darkMode} isRTL={isRTL} t={t} />;
  }

  return (
    <ActionCard dk={darkMode} isRTL={isRTL}>
      {mode === "resetPassword" && <ResetPasswordAction oobCode={oobCode} dk={darkMode} t={t} />}
      {mode !== "resetPassword" && <InvalidModeAction dk={darkMode} t={t} />}
    </ActionCard>
  );
}
